import * as React from "react"
import { makeStyles } from "tss-react/mui"
import MenuItem from "@mui/material/MenuItem"
import Checkbox from "@mui/material/Checkbox"
import InputLabel from "@mui/material/InputLabel"
import FormControl from "@mui/material/FormControl"
import ListItemText from "@mui/material/ListItemText"
import OutlinedInput from "@mui/material/OutlinedInput"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import { fetchShopsList, ShopsListResponse, ShopsQuery } from "../../../../modules/merchant/admin/shops/shopsApi"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { MenuProps } from "../constants/filterMenuConst"

type StandardMerchantShopsFilterProps = Readonly<{
    value: string[]
    setValue: (value: string[]) => void
    merchantId?: string
}>

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
    listItem: {
        textWrap: "auto",
    },
}))

function transformData(data: ShopsListResponse) {
    return data.shops
}

export function StandardMerchantShopsFilter({ merchantId, value, setValue }: StandardMerchantShopsFilterProps) {
    const { classes } = useStyles()

    const { data } = useApiQuery(fetchShopsList, {
        queryName: ShopsQuery.LIST,
        dependencies: {
            merchantId: merchantId ? +merchantId : 0,
        },
        enabled: Boolean(merchantId),
        select: transformData,
    })

    const shopMap = React.useMemo(() => {
        const map: { [id: string]: string } = {}
        data?.forEach((shop) => {
            map[shop.id] = shop.name
        })
        return map
    }, [data])

    const selectedNames = value.map((id) => shopMap[id] || "")

    const handleChange = (event: SelectChangeEvent<typeof selectedNames>) => {
        const {
            target: { value },
        } = event

        const selectedValues = typeof value === "string" ? value.split(",") : value
        const selectedIdValues = selectedValues
            .map((name) => Object.keys(shopMap).find((key) => shopMap[key] === name))
            .filter((id): id is string => id !== undefined)

        setValue(selectedIdValues)
    }

    return (
        <FormControl className={classes.input}>
            <LocalizedStrict id="standard-merchants-shops-filter-label">
                <InputLabel id="multiple-checkbox-shops-label">Shops</InputLabel>
            </LocalizedStrict>
            <Select
                labelId="multiple-checkbox-shops-label"
                id="multiple-checkbox-shops"
                multiple
                disabled={!Boolean(merchantId)}
                value={selectedNames}
                onChange={handleChange}
                input={<OutlinedInput label="Shops" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
            >
                {data?.map((shop) => (
                    <MenuItem key={shop.id} value={shop.name}>
                        <Checkbox checked={selectedNames.indexOf(shop.name) > -1} />
                        <ListItemText className={classes.listItem} primary={shop.name} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}
